import { ZodError } from 'zod'
import { ApiException } from '~/clients'
import { AxiosError } from 'axios'
import { generateErrorHtmlList } from '~/composables/useMutationWithToast'
import { isTransientApiError } from '~/utils/apiError'

type ApiError = {
  statusCode?: number
  status?: number
  data?: Record<string, string[]>
  from?: string
  type?: 'onRequestError' | 'onResponseError'
  message?: string
  cause?: ApiException | AxiosError
  result?: unknown
}

type Toast = ReturnType<typeof useToast>

function shouldSuppressGlobalToast(error: unknown): boolean {
  return isTransientApiError(error)
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.config.errorHandler = (error: unknown) => {
    if (shouldSuppressGlobalToast(error)) {
      return
    }

    const toast = useToast()
    const route = useRoute()
    const t = useNuxtApp().$i18n.t

    const errorObj = error as ApiError & { cause?: ApiException | AxiosError | ZodError }
    const { isPublicRoute } = useAuthentication()

    const handleApiException = (errorHandlerError: ApiError, toast: Toast) => {
      if (
        (errorHandlerError.statusCode === 401 || errorHandlerError.status === 401) &&
        !isPublicRoute.value
      ) {
        showError({
          message: t('error.message.unauthorized'),
          statusCode: 401,
          fatal: true,
        })
        navigateToLogin(route.fullPath)
        return
      }

      if (errorHandlerError.statusCode === 403 || errorHandlerError.status === 403) {
        showError({
          message: t('error.message.forbidden'),
          statusCode: 403,
          fatal: true,
        })
        return
      }

      if (errorHandlerError.statusCode === 404 || errorHandlerError.status === 404) {
        showError({
          message: t('error.message.notFound'),
          statusCode: 404,
          fatal: true,
        })
        return
      }
      if (errorHandlerError.statusCode === 400 || errorHandlerError.status === 400) {
        console.warn('Bad request')
        if (errorHandlerError.data) {
          toast.error('', undefined, {
            data: {
              message: t('error.message.badRequest'),
              details: generateErrorHtmlList(errorHandlerError.data),
            },
          })
        }
        return
      } else {
        toast.error(t('error.message.serverError'), undefined, {
          toastId: 'RESERVED_SERVER_ERROR_TOAST',
        })
        return
      }
    }

    if (errorObj?.cause instanceof ZodError) {
      // shouldn't happen
      console.log('Validation error')
      return
    }

    // if (error instanceof H3Error) {
    //   console.log('H3 error', error)
    //   return true
    // }

    // not catching this error, should be handled by vue query instead
    if (errorObj.from === 'api') {
      return
    }

    if (
      errorObj.cause instanceof ApiException ||
      errorObj.cause instanceof AxiosError ||
      // errorObj.cause instanceof FetchError ||
      errorObj.type === 'onRequestError' ||
      errorObj.type === 'onResponseError' ||
      errorObj.statusCode
    ) {
      return handleApiException(errorObj, toast)
    }

    return false
  }
})
